import React, { useEffect, useState } from "react";
import { config } from "../../CommonUtils/CommonURLS";
import { CommonAxiosGet } from "../../CommonUtils/CommonAxios";
import { ErrorMessage, Field, Form, FormikProvider, useFormik } from "formik";
import CommonReactTable from "../../CommonUtils/CommonReactTable";
import { FiCheckCircle, FiXCircle } from "react-icons/fi";
import * as bie from "react-bootstrap";
import { Columns } from "lucide-react";
import { getServiceFullName } from "../../CommonUtils/CommonValidations";
import { useSelector } from "react-redux";
import { GetNocServices } from "../../Reducers/AffiliationCommonservice";


const AddressCell = ({ value }) => {
  const [open, setOpen] = useState(false);

  if (!value) return <span>-</span>;

  return (
    <div style={{ position: "relative" }}>
      <div
        role="button"
        tabIndex={0}
        style={{
          maxWidth: "180px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "pointer",
          color: "#0d6efd",
          textDecoration: "underline dotted",
        }}
        title="Click to view full address"
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {value}
      </div>

      {open && (
        <div
          role="button"
          tabIndex={0}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen(true);
            }
          }}
        >
          <div
            role="button"
            tabIndex={0}
            style={{
              background: "#fff",
              borderRadius: "8px",
              padding: "1.25rem 1.5rem",
              maxWidth: "420px",
              width: "90%",
              boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
              }
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
              <strong style={{ fontSize: "15px", color: "#333" }}>Institute Address</strong>
              <button
                onClick={() => setOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "20px",
                  cursor: "pointer",
                  color: "#888",
                  lineHeight: 1,
                }}
                title="Close"
              >
                &times;
              </button>
            </div>
            <p style={{ margin: 0, fontSize: "14px", color: "#444", lineHeight: "1.6" }}>{value}</p>
          </div>
        </div>
      )}
    </div>
  );
};
export default function ApplicationStatusReport() {
  const [status, setStatus] = useState([]);
  const [isFetched, setIsFetched] = useState(false);
  const [selectedNocType, setSelectedNocType] = useState("");
  const NocServicesOptions = useSelector((state) => state?.commonMastersReducer?.nocservices);

  function GetStatus(noctype) {
    setSelectedNocType(noctype);
    if (!noctype) {
      setIsFetched(false);
      return;
    }
    setIsFetched(false);
    CommonAxiosGet(`${config.url.FAC_URL}getNocTypeBasedStatusReport?nocType=${noctype}`)
      .then((res) => {
        const data = res?.data?.scode === "01" ? (res?.data?.data || []).map((item) => ({ ...item, scrutiny: getScrutinyStatus(item) })) : [];
        setStatus(data);
        setIsFetched(true);
      })
      .catch(() => {
        setStatus([]);
        setIsFetched(true);
      });
  }


  const [applicationCount, setApplicationCount] = useState(0);
  function GetOverallApplicationCount(noctype) {
    let url = config?.url?.FAC_URL + "getoverallconsolidated/" + noctype;

    CommonAxiosGet(url).then((res) => {
      if (res?.data?.scode === "01") {
        setApplicationCount(res?.data?.noc_services[0] || []);

      } else {
        setApplicationCount([]);
      }
    });
  }
  const total = applicationCount?.total_count || 0;
  const approved = applicationCount?.approved_count || 0;
  const conditionalApproved = applicationCount?.condition_approved || 0;
  const pending = total - (approved + conditionalApproved);
  const rejected = applicationCount?.shifting_rejected || 0;
  const def1 = applicationCount?.phase1_def_count || 0;
  const def2 = applicationCount?.phase2_def_count || 0;
  const TotalApproved = approved + conditionalApproved;


  const renderStatus = (value) => {
    const isDone = String(value).toLowerCase() === "done";
    return isDone ? <FiCheckCircle color="#28a745" size={18} title="Done" /> : <FiXCircle color="#dc3545" size={18} title="Not Done" />;
  };

  const getYesCount = (key) => {
    return status?.filter((item) => item[key] === "Done").length;
  };

  // Function to check if Phase 2 should show "-" instead of actual data
  const shouldHidePhase2Data = (row) => {
    const nocType = row?.noc_type || selectedNocType;
    const finalRemarks = row?.final_remarks || "";

    // ONLY for EOP and Management
    if (nocType === "eop" || nocType === "management") {
      // Check if final_remarks is "Approved" OR "Conditionally Approved"
      if (finalRemarks === "Approved" || finalRemarks === "Conditionally Approved") {
        return true; // Hide Phase 2 data, show "-"
      }
    }

    // For all other cases, show actual data
    return false;
  };

  // Check if Phase 2 column should be shown at all
  const shouldShowPhase2Column = () => {
    if (isShifting) return false;
    if (status.length === 0) return true;

    // For all NOC Types except shifting, show Phase 2
    return true;
  };

  const isShifting = status?.some((item) => item.noc_type === "shifting");

  // Function to render Phase 2 status with condition
  const renderPhase2Status = (row, key) => {
    const value = row?.original?.[key] || "";

    // Check if we should show "-" instead of actual data
    if (shouldHidePhase2Data(row?.original)) {
      return <span style={{ color: "#999", fontWeight: "bold" }}>-</span>;
    }

    // Show actual data
    return renderStatus(value);
  };

  // Function to render Phase 2 remarks with condition
  const renderPhase2Remarks = (row) => {
    const value = row?.original?.final_remarks_phase2 || "";

    // Check if we should show "-" instead of actual data
    if (shouldHidePhase2Data(row?.original)) {
      return <span style={{ color: "#999", fontWeight: "bold" }}>-</span>;
    }

    // Show actual data with colors
    let color = "";
    let bgColor = "";

    if (value === "Approved") {
      color = "#2e7d32";
      bgColor = "#e8f5e9";
    } else if (value === "Deficiencies Identified") {
      color = "#6e4234";
      bgColor = "#efebe9";
    } else if (value === "Conditionally Approved") {
      color = "#e96711";
      bgColor = "#ffebee";
    }

    return (
      <span
        style={{
          color: color,
          backgroundColor: bgColor,
          fontWeight: "bold",
          padding: "4px 12px",
          borderRadius: "4px",
          display: "inline-block",
        }}
      >
        {value ? value : "-"}
      </span>
    );
  };

  const getScrutinyStatus = (item) => {
    if (item.noc_type === "management") return item.management_scrutiny;
    if (item.noc_type === "eop") return item.eop_scrutiny;
    if (item.noc_type === "shifting") return item.shifting_scrutiny;
    return "Not Done";
  };

  const StatusColumns = [
    {
      id: "slNo",
      header: "Sl.No",
      cell: ({ row }) => row.index + 1,
    },
    {
      id: "Phase1",
      header: "Phase1",
      columns: [
        {
          id: "ub_name",
          header: "University Name",
          accessorKey: "ub_name",
        },
        {
          id: "inst_name",
          header: "Name of the Institute",
          accessorFn: (row) => `${row.inst_name} - ${row.coll_code}`,
          Footer: "Total",
          cell: ({ row }) => (
            <span>
              {row?.original?.inst_name} - {row?.original?.coll_code}
            </span>
          ),
        },
        {
          id: "inst_address",
          header: "Institute Address",
          accessorKey: "inst_address",
          cell: ({ getValue }) => {
            const value = getValue() || "";
            return <AddressCell value={value} />;
          },
        },
        {
          id: "college_done",
          header: "College Done",
          accessorKey: "college_done",
          cell: ({ getValue }) => renderStatus(getValue()),
          Footer: () => getYesCount("college_done"),
        },
        ...(isShifting
          ? []
          : [
            {
              id: "scrutiny",
              header: "Scrutiny",
              cell: ({ row }) => renderStatus(getScrutinyStatus(row.original)),
              Footer: () => status?.filter((item) => String(getScrutinyStatus(item))?.toLowerCase() === "done").length,
            },
          ]),
        ...(isShifting
          ? [
            {
              id: "online_shifting_scrutiny",
              header: "Online Shifting Scrutiny",
              accessorKey: "online_shifting_scrutiny",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("online_shifting_scrutiny"),
            },
          ]
          : []),

        ...(isShifting
          ? []
          : [
            {
              id: "jd_approved",
              header: "JD Approved",
              accessorKey: "jd_approved",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("jd_approved"),
            },
            {
              id: "jd_status",
              header: "JD Status",
              accessorKey: "final_remarks",
              cell: ({ row }) => {
                // Check if shifting, then use final_remarks_phase2, else use final_remarks
                const value = isShifting ? row.original.final_remarks_phase2 : row.original.final_remarks;

                let color = "";
                let bgColor = "";

                if (value === "Approved") {
                  color = "#2e7d32";
                  bgColor = "#e8f5e9";
                } else if (value === "Deficiencies Identified") {
                  color = "#6e4234";
                  bgColor = "#efebe9";
                } else if (value === "Conditionally Approved") {
                  color = "#e96711";
                  bgColor = "#ffebee";
                } else if (value === "Rejected") {
                  color = "#e40a0a";
                  bgColor = "#ffebee";
                }

                return (
                  <span
                    style={{
                      color: color,
                      backgroundColor: bgColor,
                      fontWeight: "bold",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      display: "inline-block",
                    }}
                  >
                    {value ? value : "-"}
                  </span>
                );
              },
            },
            {
              id: "section_approved",
              header: "Section Forwarded",
              accessorKey: "section_approved",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("section_approved"),
            },
            {
              id: "final_status_by_jd",
              header: "Final Status By JD",
              accessorKey: "final_status_by_jd",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("final_status_by_jd"),
            },
            {
              id: "digi_sign_done",
              header: "Digital Sign Done",
              accessorKey: "digi_sign_done",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("digi_sign_done"),
            },

          ]),
      ],
    },

    // Phase 2 - Only show if shifting
    ...(isShifting
      ? [
        {
          id: "ShiftingPhase2",
          header: "Phase2",
          columns: [
            {
              id: "recommended_physical",
              header: "Recommended Physical",
              accessorKey: "recommended_physical",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("recommended_physical"),
            },
            {
              id: "shifting_approvedby_section",
              header: "Shifting Approved By Section",
              accessorKey: "shifting_approvedby_section",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("shifting_approvedby_section"),
            },
            {
              id: "physical_shifting_scrutiny",
              header: "Physical Shifting Scrutiny",
              accessorKey: "physical_shifting_scrutiny",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("physical_shifting_scrutiny"),
            },
            {
              id: "jd_approved",
              header: "JD Approved",
              accessorKey: "jd_approved",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("jd_approved"),
            },
            {
              id: "jd_status",
              header: "JD Status",
              accessorKey: "final_remarks",
              cell: ({ row }) => {
                // Check if shifting, then use final_remarks_phase2, else use final_remarks
                const value = isShifting ? row.original.final_remarks_phase2 : row.original.final_remarks;

                let color = "";
                let bgColor = "";

                if (value === "Approved") {
                  color = "#2e7d32";
                  bgColor = "#e8f5e9";
                } else if (value === "Deficiencies Identified") {
                  color = "#6e4234";
                  bgColor = "#efebe9";
                } else if (value === "Conditionally Approved") {
                  color = "#e96711";
                  bgColor = "#ffebee";
                } else if (value === "Rejected") {
                  color = "#e40a0a";
                  bgColor = "#ffebee";
                }

                return (
                  <span
                    style={{
                      color: color,
                      backgroundColor: bgColor,
                      fontWeight: "bold",
                      padding: "4px 12px",
                      borderRadius: "4px",
                      display: "inline-block",
                    }}
                  >
                    {value ? value : "-"}
                  </span>
                );
              },
            },
            {
              id: "section_approved",
              header: "Section Forwarded",
              accessorKey: "section_approved",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("section_approved"),
            },
            {
              id: "final_status_by_jd",
              header: "Final Status By JD",
              accessorKey: "final_status_by_jd",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("final_status_by_jd"),
            },
            // {
            //   id: "digi_sign_done",
            //   header: "Digital Sign Done",
            //   accessorKey: "digi_sign_done",
            //   cell: ({ getValue }) => renderStatus(getValue()),
            //   Footer: () => getYesCount("digi_sign_done"),
            // },
            {
              id: "digi_sign_done_phase2",
              header: "Digital Sign Done",
              accessorKey: "digi_sign_done_phase2",
              cell: ({ getValue }) => renderStatus(getValue()),
              Footer: () => getYesCount("digi_sign_done_phase2"),
            },
          ],
        },
      ]
      : []),

    // Phase 2 - Only show if not shifting
    ...(!isShifting && shouldShowPhase2Column()
      ? [
        {
          id: "Phase2",
          header: "Phase2",
          columns: [
            {
              id: "college_def",
              header: "College Deficiencies Done",
              cell: ({ row }) => renderPhase2Status(row, "college_def"),
              accessorKey: "college_def",
              Footer: () => getYesCount("college_def"),
            },
            {
              id: "phase2_scrutiny",
              header: "Phase2 Scrutiny",
              cell: ({ row }) => renderPhase2Status(row, "phase2_scrutiny"),
              accessorKey: "phase2_scrutiny",
              Footer: () => getYesCount("phase2_scrutiny"),
            },
            {
              id: "jd_approved_phase2",
              header: "JD Approved",
              cell: ({ row }) => renderPhase2Status(row, "jd_approved_phase2"),
              accessorKey: "jd_approved_phase2",
              Footer: () => getYesCount("jd_approved_phase2"),
            },
            {
              id: "jd_status_phase2",
              header: "JD Status",
              accessorKey: "final_remarks_phase2",
              cell: ({ row }) => renderPhase2Remarks(row),
            },
            {
              id: "section_approved_phase2",
              header: "Section Forwarded",
              cell: ({ row }) => renderPhase2Status(row, "section_approved_phase2"),
              accessorKey: "section_approved_phase2",
              Footer: () => getYesCount("section_approved_phase2"),
            },
            {
              id: "final_status_by_jd_phase2",
              header: "Final Status By JD",
              cell: ({ row }) => renderPhase2Status(row, "final_status_by_jd_phase2"),
              accessorKey: "final_status_by_jd_phase2",
              Footer: () => getYesCount("final_status_by_jd_phase2"),
            },
            {
              id: "digi_sign_done_phase2",
              header: "Digital Sign Done",
              cell: ({ row }) => renderPhase2Status(row, "digi_sign_done_phase2"),
              accessorKey: "digi_sign_done_phase2",
              Footer: () => getYesCount("digi_sign_done_phase2"),
            },
          ],
        },
      ]
      : []),

    {
      id: "proceeding_count",
      header: "Total Approved",
      accessorKey: "proceeding_count",
      cell: ({ getValue }) => renderStatus(getValue()),
      Footer: () => getYesCount("proceeding_count"),
    },
  ];

  const InstitutionFormik = useFormik({
    initialValues: {
      nocType: "",
    },
    enableReinitialize: true,
    onSubmit: (val) => {
      console.log("val", val);
    },
  });
  useEffect(() => {
    GetNocServices();
  }, []);



  return (
    <div className="faculty-form-wrapper">
      <div className="faculty-card">
        <h5 className="mb-3 innerheader-new">
          <span>Total Application Status Report</span>
        </h5>

        <FormikProvider value={InstitutionFormik}>
          <Form className="form_adjust" onSubmit={InstitutionFormik.handleSubmit} onChange={InstitutionFormik.handleChange}>
            <bie.Row>
              <bie.Col xs={12} sm={12} md={12} lg={6} xl={6} xxl={6} className="mb-2">
                <div className="d-flex align-items-center">
                  <label className="me-2 mb-0">Select NOC Type :</label>
                  <Field
                    as="select"
                    name="nocType"
                    className="form-control"
                    style={{ width: "250px" }}
                    onChange={(e) => {
                      InstitutionFormik.handleChange(e);
                      GetStatus(e.target.value);
                      GetOverallApplicationCount(e.target.value);
                    }}
                  >
                    <option value="">--Select--</option>

                    {NocServicesOptions
                      ?.filter((service) => service.is_active)
                      .map((service) => (
                        <option
                          key={service.nos_short_name}
                          value={service.nos_short_name}
                        >
                          {service.full_name}
                        </option>
                      ))}
                  </Field>
                </div>
                <ErrorMessage name="nocType" component="div" className="text-danger" />
              </bie.Col>
            </bie.Row>
            {!isFetched ? null : status?.length > 0 ? (

              <div className="pvp-root" style={{ textAlign: "center" }}>

                <div className="dashboard-summary">

                  {/* Total */}
                  <div className="kpi-card total-card">
                    <div className="kpi-icon">
                      <i className="bi bi-grid-3x3-gap-fill"></i>
                    </div>

                    <div className="kpi-content">
                      <div className="kpi-title">Total Applications</div>
                      <div className="kpi-value">{total}</div>
                    </div>
                  </div>

                  {/* Approved */}
                  <div className="kpi-card approved-card">

                    <div className="card-heading">
                      <i className="bi bi-check-circle-fill me-2"></i>
                      Approved ({TotalApproved})
                    </div>

                    <div className="split-section">

                      <div className="split-item">
                        <div className="split-title">
                          Approved
                        </div>

                        <div className="split-value">
                          {approved}
                        </div>
                      </div>

                      <div className="split-line"></div>

                      <div className="split-item">
                        <div className="split-title">
                          Conditional
                        </div>

                        <div className="split-value">
                          {conditionalApproved}
                        </div>
                      </div>

                    </div>

                  </div>


                  {/* Rejected */}

                  {InstitutionFormik?.values?.nocType === "shifting" && (

                    <div className="kpi-card rejected-card">

                      <div className="kpi-icon">
                        <i className="bi bi-x-circle-fill"></i>
                      </div>

                      <div className="kpi-content">
                        <div className="kpi-title">
                          Rejected
                        </div>

                        <div className="kpi-value">
                          {rejected}
                        </div>
                      </div>

                    </div>


                  )}

                  {/* Deficiency */}

                  {(InstitutionFormik?.values?.nocType === "eop" ||
                    InstitutionFormik?.values?.nocType === "management") && (

                      <div className="kpi-card deficiency-card">

                        <div className="card-heading">
                          <i className="bi bi-exclamation-diamond-fill me-2"></i>
                          Deficiency
                        </div>

                        <div className="split-section">

                          <div className="split-item">
                            <div className="split-title">
                              Phase 1
                            </div>

                            <div className="split-value">
                              {def1}
                            </div>
                          </div>

                          <div className="split-line"></div>

                          <div className="split-item">
                            <div className="split-title">
                              Phase 2
                            </div>

                            <div className="split-value">
                              {def2}
                            </div>
                          </div>

                        </div>

                      </div>

                    )}
                  {/* Pending */}
                  <div className="kpi-card pending-card">

                    <div className="kpi-icon">
                      <i className="bi bi-hourglass-split"></i>
                    </div>

                    <div className="kpi-content">
                      <div className="kpi-title">
                        Pending
                      </div>

                      <div className="kpi-value">
                        {pending}
                      </div>
                    </div>

                  </div>
                </div>


                <CommonReactTable
                  data={status}
                  columns={StatusColumns}
                  filename="NOC STATUS REPORT"
                  headerName={`Total Application Status Report - ${getServiceFullName(
                    InstitutionFormik?.values?.nocType,
                    NocServicesOptions
                  )}`}
                  showFooter={true}
                  showPagination={false}
                  showExcel={true}
                  showPrint={true}
                  excelFileName="NOC Status Report"
                />
              </div>

            ) : (
              <center className="text-danger mt-4 h5">** No Data Found **</center>
            )}
          </Form>
        </FormikProvider>
      </div>
    </div >
  );
}
